import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, FolderOpen, BarChart3, FileText, Settings, Building2, Shield, TestTube, Database, Users, Activity, DollarSign, Briefcase } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubItem, SidebarMenuSubButton, useSidebar } from "./ui/sidebar";
import { useQuickStats } from '@/hooks/useQuickStats';
import { useDevMode } from '@/hooks/useDevMode';
import { DevModeToggle } from './DevModeToggle';
import { useActiveCases } from '@/hooks/useActiveCases';
import { MODULES_ORDERED } from '@/lib/modules';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { NotificationBell } from './NotificationBell';
interface Organization {
  id: string;
  name: string;
  login?: string;
  avatar_url?: string;
  [key: string]: unknown;
}
interface AppSidebarProps {
  organization: Organization | null;
}
export function AppSidebar({
  organization
}: AppSidebarProps) {
  const {
    state
  } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t
  } = useTranslation('common');
  const { effectiveRole, isSimulating, canUseDevMode } = useDevMode();
  
  // Get real-time statistics from database
  const { stats, loading: statsLoading } = useQuickStats();

  // Dev tools visible only for system_admin when NOT simulating
  const isDevToolsVisible = effectiveRole === 'system_admin' && !isSimulating;

  // Navigation items for the revision app
  const navigationItems = [{
    title: t('navigation.dashboard'),
    url: "/",
    icon: LayoutDashboard,
    description: t('sidebar.description.dashboard')
  }, {
    title: t('navigation.cases'),
    url: "/cases",
    icon: FolderOpen,
    description: t('sidebar.description.cases')
  }, {
    title: t('navigation.queue'),
    url: "/queue",
    icon: BarChart3,
    description: t('sidebar.description.queue')
  }, {
    title: "Statistik",
    url: "/statistics",
    icon: BarChart3,
    description: "Visa grafer och trender över tid"
  }, {
    title: t('navigation.team'),
    url: "/team",
    icon: Building2,
    description: t('sidebar.description.team')
  }, {
    title: t('navigation.billing'),
    url: "/billing",
    icon: FileText,
    description: t('sidebar.description.billing')
  }, {
    title: t('navigation.settings'),
    url: "/settings",
    icon: Settings,
    description: t('sidebar.description.settings')
  }];

  // Development navigation items (conditional - only for system_admin when not simulating)
  const devNavigationItems = [];
  if (isDevToolsVisible) {
    devNavigationItems.push({
      title: t('testlab.title'),
      url: "/testlab",
      icon: TestTube,
      description: "Intern testmiljö"
    });
    devNavigationItems.push({
      title: "DB Health",
      url: "/admin/database-health",
      icon: Database,
      description: "Database diagnostik"
    });
    devNavigationItems.push({
      title: "Hantera testare",
      url: "/admin/testers",
      icon: Users,
      description: "Registrera"
    });
    devNavigationItems.push({
      title: "Tester-aktivitet",
      url: "/admin/tester-activity",
      icon: Activity,
      description: "Spåra aktivitet"
    });
    devNavigationItems.push({
      title: "AI-kostnader",
      url: "/admin/ai-costs",
      icon: DollarSign,
      description: "Token & kostnadsanalys"
    });
  }
  const isCollapsed = state === "collapsed";

  // Get active cases for module navigation
  const { cases: activeCases } = useActiveCases();

  // Extract case ID from current URL
  const caseMatch = location.pathname.match(/^\/cases\/([a-f0-9-]{36})/);
  const activeCaseId = caseMatch?.[1] || null;
  const currentCase = activeCases.find(c => c.id === activeCaseId);

  // Get selected module from URL query params
  const searchParams = new URLSearchParams(location.search);
  const selectedModule = searchParams.get('module');

  // Helper to check if current route matches
  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  // Helper to check if a module is active
  const isModuleActive = (moduleId: string) => {
    return selectedModule === moduleId;
  };
  return <Sidebar className={isCollapsed ? "w-16" : "w-64"}>
      <SidebarContent className="border-r border-gray-200 bg-white">
        {/* App branding */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/lovable-uploads/f1bb597c-c583-4985-ae82-477955b7b8c0.png" alt="AuditAI" className="h-32 w-auto flex-shrink-0" />
            </div>
            {!isCollapsed && <NotificationBell />}
          </div>
        </div>

        {/* Organization info */}
        {!isCollapsed && organization && <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700 truncate">
                {organization.name}
              </span>
            </div>
          </div>}

        {/* Active case selector - always visible when expanded */}
        {!isCollapsed && (
          <div className="px-4 py-3 border-b border-gray-200 bg-blue-50/50">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="h-4 w-4 text-blue-600" />
              <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                Aktivt ärende
              </span>
            </div>
            <Select 
              value={activeCaseId || 'none'} 
              onValueChange={(caseId) => {
                if (caseId === 'none') {
                  navigate('/cases');
                } else {
                  navigate(`/cases/${caseId}?module=data_quality`);
                }
              }}
            >
              <SelectTrigger className="w-full h-9 text-sm bg-white border-blue-200 hover:border-blue-300 focus:ring-blue-500">
                <SelectValue>
                  {activeCaseId 
                    ? (currentCase?.title || 'Laddar...') 
                    : 'Välj ärende...'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-white z-50 border border-gray-200 shadow-lg">
                <SelectItem value="none" className="text-gray-500 italic">
                  Inget ärende valt
                </SelectItem>
                {activeCases.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    <div className="flex items-center gap-2">
                      <span className="truncate">{c.title}</span>
                      {c.id === activeCaseId && (
                        <span className="text-blue-600 font-bold">•</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-gray-500 font-medium">{t('navigation.dashboard')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map(item => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} className={({
                      isActive: linkActive
                    }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${linkActive || isActive(item.url) ? "bg-blue-50 text-blue-700 border-l-4 border-blue-600 font-medium" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"}`}>
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                      {!isCollapsed && <div>
                        <span className="text-sm font-medium">{item.title}</span>
                        <p className="text-xs opacity-75">{item.description}</p>
                      </div>}
                    </NavLink>
                  </SidebarMenuButton>
                  
                  {/* Show module sub-items when on a case page and this is the Cases menu item */}
                  {item.url === "/cases" && activeCaseId && !isCollapsed && (
                    <SidebarMenuSub>
                      {/* Current case indicator */}
                      <div className="px-3 py-1.5 text-xs font-medium text-gray-500 truncate border-b border-gray-100 mb-1">
                        {currentCase?.title || 'Ärende'}
                      </div>
                      
                      {/* All modules rendered dynamically */}
                      {MODULES_ORDERED.map(module => (
                        <SidebarMenuSubItem key={module.id}>
                          <SidebarMenuSubButton asChild>
                            <NavLink 
                              to={module.isSieModule 
                                ? `/cases/${activeCaseId}/sie-accounting-basis`
                                : `/cases/${activeCaseId}?module=${module.id}`
                              }
                              className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded transition-colors ${
                                module.isSieModule
                                  ? (location.pathname.includes('/sie-accounting-basis')
                                      ? 'bg-emerald-50 text-emerald-700 font-medium' 
                                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700')
                                  : (isModuleActive(module.id) 
                                      ? 'bg-blue-50 text-blue-700 font-medium' 
                                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700')
                              }`}
                            >
                              <span className="w-4 text-xs text-gray-400 font-mono">{module.displayOrder}.</span>
                              <span className="truncate">{t(module.nameKey)}</span>
                            </NavLink>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  )}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Development Navigation (conditional) */}
        {devNavigationItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-gray-500 font-medium">Utveckling</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {devNavigationItems.map(item => 
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink to={item.url} className={({
                        isActive: linkActive
                      }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${linkActive || isActive(item.url) ? "bg-amber-50 text-amber-700 border-l-4 border-amber-600 font-medium" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"}`}>
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        {!isCollapsed && <div>
                            <span className="text-sm font-medium">{item.title}</span>
                            <p className="text-xs opacity-75">{item.description}</p>
                          </div>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Quick stats (only show when expanded) */}
        {!isCollapsed && <SidebarGroup>
            <SidebarGroupLabel className="text-gray-500 font-medium">{t('sidebar.quickstats.title')}</SidebarGroupLabel>
            <SidebarGroupContent>
              <div className="px-3 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{t('sidebar.quickstats.cases')}</span>
                  <span className="font-medium text-gray-900">
                    {statsLoading ? '...' : stats.cases}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{t('sidebar.quickstats.documents')}</span>
                  <span className="font-medium text-gray-900">
                    {statsLoading ? '...' : stats.documents}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{t('sidebar.quickstats.analyses')}</span>
                  <span className="font-medium text-gray-900">
                    {statsLoading ? '...' : stats.analyses}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{t('sidebar.quickstats.reports')}</span>
                  <span className="font-medium text-gray-900">
                    {statsLoading ? '...' : stats.reports}
                  </span>
                </div>
              </div>
            </SidebarGroupContent>
          </SidebarGroup>}

        {/* Policies section at bottom */}
        <div className="mt-auto border-t border-gray-100">
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink to="/policies" className={({
                    isActive: linkActive
                  }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${linkActive || isActive("/policies") ? "bg-blue-50 text-blue-700 border-l-4 border-blue-600 font-medium" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"}`}>
                      <Shield className="h-4 w-4 flex-shrink-0" />
                      {!isCollapsed && <div>
                          <span className="text-sm font-medium">{t('policies.title')}</span>
                          <p className="text-xs opacity-75">{t('sidebar.policies.description')}</p>
                        </div>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </div>

        {/* Dev Mode Toggle (only for system_admin) */}
        {!isCollapsed && canUseDevMode && <DevModeToggle />}

      </SidebarContent>
    </Sidebar>;
}